import React, { useEffect, useState } from 'react';
import {
  Divider,
  Grid,
  Header,
  Icon,
  Loader,
  Message,
  Modal,
  Segment
} from 'semantic-ui-react';

import Button from '../../../components/Button';
import ChargeDistributionSummary from './ChargeDistributionSummary';
import { dangerNotification } from '../../../services/notification';
import { toAbsoluteUrl } from '../../../utils/toAbsoluteUrl';
import { maskCpfOrCnpj, maskMoney, maskPhoneNumber, maskZipCode } from '../../../services/masks';
import { getFiles } from '../../../services/file';
import {
  getStandaloneCharge,
  StandaloneChargeDetailDTO,
  StandaloneChargePaymentMethodEnum,
  StandaloneChargeStatusEnum
} from '../../../services/standalone-charges';
import { getPayerDocumentLabel } from '../utils/payerTypeHelpers';
import { ChargeRefundData } from '../types';

type RefundAttachment = {
  id?: string;
  name?: string;
  link?: string;
  key?: string;
};

type Props = {
  open: boolean;
  chargeId: string | null;
  statusLabelMap: Record<string, string>;
  /** Usuário de imobiliária: oculta bloco Imobiliária (contexto já é a própria imobiliária). */
  hideEstateSection?: boolean;
  refund?: ChargeRefundData;
  onClose: () => void;
  onCopyLink: (link: string, chargeId?: string) => void;
};

const formatDateTime = (value?: string | Date | null): string => {
  if (value === undefined || value === null || value === '') return '-';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
};

const getEstimatedPaidAt = (charge: StandaloneChargeDetailDTO): string | null => {
  if (
    charge.status !== StandaloneChargeStatusEnum.paid &&
    charge.status !== StandaloneChargeStatusEnum.refunded
  ) return null;
  if (charge.paidAt) return null;
  const raw = charge.lastGatewayPayload?.lastUpdateAt;
  if (!raw) return null;
  const formatted = formatDateTime(raw);
  return formatted === '-' ? null : formatted;
};

type PayerAddressFields = {
  street: string;
  number: string;
  neighborhood: string;
  complement: string;
  zipCode: string;
  city: string;
  state: string;
  country: string;
};

/** API pode serializar em camelCase ou snake_case conforme pipeline do backend. */
const pickStr = (raw: Record<string, unknown>, camel: string, snake: string): string => {
  const c = raw[camel];
  const s = raw[snake];
  if (c != null && String(c).trim() !== '') return String(c).trim();
  if (s != null && String(s).trim() !== '') return String(s).trim();
  return '';
};

const pickPayerAddress = (detail: StandaloneChargeDetailDTO): PayerAddressFields => {
  const raw = detail as unknown as Record<string, unknown>;
  const country = pickStr(raw, 'payerAddressCountry', 'payer_address_country');
  return {
    street: pickStr(raw, 'payerAddressStreet', 'payer_address_street'),
    number: pickStr(raw, 'payerAddressNumber', 'payer_address_number'),
    neighborhood: pickStr(raw, 'payerAddressNeighborhood', 'payer_address_neighborhood'),
    complement: pickStr(raw, 'payerAddressComplement', 'payer_address_complement'),
    zipCode: pickStr(raw, 'payerAddressZipCode', 'payer_address_zip_code'),
    city: pickStr(raw, 'payerAddressCity', 'payer_address_city'),
    state: pickStr(raw, 'payerAddressState', 'payer_address_state'),
    country: country || 'BR'
  };
};

const hasAnyPayerAddress = (a: PayerAddressFields): boolean =>
  [a.street, a.number, a.neighborhood, a.complement, a.zipCode, a.city, a.state].some(
    s => s.trim().length > 0
  );

const getPayerAddressLine = (a: PayerAddressFields): string => {
  const line = [a.street, a.number].filter(Boolean).join(', ');
  return line.trim() ? line : '-';
};

/** Boleto sempre exibe o bloco (endereço obrigatório na criação); Pix só se houver dados. */
const showPayerAddressSection = (detail: StandaloneChargeDetailDTO): boolean => {
  const method = String(detail.paymentMethod || '').toUpperCase();
  if (method === StandaloneChargePaymentMethodEnum.boleto || method === 'BOLETO') {
    return true;
  }
  return hasAnyPayerAddress(pickPayerAddress(detail));
};

const fieldLabelStyle: React.CSSProperties = {
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'rgba(0,0,0,0.55)',
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  marginBottom: 4
};

const sectionHeaderStyle: React.CSSProperties = {
  color: '#5B21B6',
  borderBottom: '1px solid #D8B4FE',
  paddingBottom: 8,
  marginBottom: 16
};

const FieldBlock: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={fieldLabelStyle}>{label}</div>
    <div>{children}</div>
  </div>
);

const getRefundAttachmentLabel = (attachment: RefundAttachment, index: number): string => {
  if (attachment.name && attachment.name !== 'refund_attachment') {
    return attachment.name;
  }
  if (attachment.key) {
    return attachment.key.split('_').slice(1).join('_') || attachment.key;
  }
  return index === 0 ? 'Comprovante' : `Comprovante ${index + 1}`;
};

const ChargeDetailsModal: React.FC<Props> = ({
  open,
  chargeId,
  statusLabelMap,
  hideEstateSection = false,
  refund,
  onClose,
  onCopyLink
}) => {
  const [detail, setDetail] = useState<StandaloneChargeDetailDTO | null>(null);
  const [refundAttachments, setRefundAttachments] = useState<Record<string, RefundAttachment[]>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !chargeId) {
      setDetail(null);
      setRefundAttachments({});
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    getStandaloneCharge(chargeId)
      .then(async data => {
        if (cancelled) return;
        setDetail(data);

        const refunds = data.refunds || [];
        if (refunds.length === 0) {
          setRefundAttachments({});
          return;
        }

        const entries = await Promise.all(
          refunds
            .filter(item => item.id)
            .map(async item => {
              try {
                const response = await getFiles({
                  tableName: 'standalone_charge_refunds',
                  registerId: item.id,
                  name: 'refund_attachment'
                });
                return [item.id, response?.urls || []] as [string, RefundAttachment[]];
              } catch {
                return [item.id, []] as [string, RefundAttachment[]];
              }
            })
        );

        if (!cancelled) {
          setRefundAttachments(Object.fromEntries(entries));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError('Não foi possível carregar os detalhes da cobrança.');
          dangerNotification('Erro', 'Não foi possível carregar os detalhes da cobrança.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, chargeId]);

  const estimatedPaidAt = detail ? getEstimatedPaidAt(detail) : null;
  const payerAddr = detail ? pickPayerAddress(detail) : null;
  const refundsToShow = detail
    ? [...(detail.refunds || [])]
      .sort((a, b) => {
        const aTime = new Date(a.createdAt || detail.refundedAt || 0).getTime();
        const bTime = new Date(b.createdAt || detail.refundedAt || 0).getTime();
        return aTime - bTime;
      })
      .map((item, index) => ({
        id: item.id || `${detail.id}-${index}`,
        chargeId: detail.id,
        amount: Number(item.amount || 0),
        description: item.description || '',
        refundedAt: item.createdAt || detail.refundedAt || '',
        attachments: item.id ? refundAttachments[item.id] || [] : []
      }))
    : [];
  const hasRefund = refundsToShow.length > 0;
  const shouldShowChargeLink =
    detail?.status !== StandaloneChargeStatusEnum.refunded && !hasRefund;

  const handleDownloadAttachment = (attachment: RefundAttachment) => {
    if (!attachment.link) return;
    window.open(attachment.link, '_blank', 'noopener,noreferrer');
  };

  return (
    <Modal open={open} onClose={onClose} size="large" closeIcon>
      <Modal.Header>
        <Icon name="tag" style={{ color: '#5B21B6' }} />
        Detalhes da cobrança
      </Modal.Header>
      <Modal.Content scrolling>
        {loading && (
          <Segment basic style={{ minHeight: 160 }}>
            <Loader active inline="centered">
              Carregando…
            </Loader>
          </Segment>
        )}
        {!loading && error && <Message negative>{error}</Message>}
        {!loading && !error && detail && payerAddr && (
          <>
            {hasRefund && (
              <>
                <Header as="h4" style={{ ...sectionHeaderStyle, marginTop: 0 }}>
                  Detalhes do estorno
                </Header>
                {refundsToShow.map((refundItem, index) => (
                  <div
                    key={`${refundItem.id}-${refundItem.refundedAt}`}
                    style={{
                      marginBottom: index < refundsToShow.length - 1 ? 10 : 16,
                      paddingBottom: index < refundsToShow.length - 1 ? 10 : 0,
                      borderBottom:
                        index < refundsToShow.length - 1
                          ? '1px solid rgba(216, 180, 254, 0.55)'
                          : undefined
                    }}
                  >
                    <FieldBlock label="Valor estornado">
                      {`R$ ${maskMoney(String(Number(refundItem.amount).toFixed(2)), true)}`}
                    </FieldBlock>
                    <FieldBlock label="Descrição">
                      <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                        {refundItem.description || '-'}
                      </span>
                    </FieldBlock>
                    <FieldBlock label="Anexos">
                      {refundItem.attachments.length > 0 ? (
                        <div style={{ display: 'grid', gap: 6, maxWidth: 440 }}>
                          {refundItem.attachments.map((attachment, attachmentIndex) => (
                            <div
                              key={attachment.id || attachment.key || attachmentIndex}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: 'minmax(0, 1fr) 32px',
                                alignItems: 'center',
                                columnGap: 8,
                                minHeight: 30
                              }}
                            >
                              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <Icon name="file outline" style={{ color: '#5B21B6' }} />
                                {getRefundAttachmentLabel(attachment, attachmentIndex)}
                              </span>
                              <Button
                                type="button"
                                icon="download"
                                basic
                                size="mini"
                                style={{ padding: '7px 9px' }}
                                aria-label={`Baixar ${getRefundAttachmentLabel(attachment, attachmentIndex)}`}
                                disabled={!attachment.link}
                                onClick={() => handleDownloadAttachment(attachment)}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        '-'
                      )}
                    </FieldBlock>
                  </div>
                ))}
              </>
            )}

            <ChargeDistributionSummary detail={detail} />

            <Header as="h4" style={sectionHeaderStyle}>
              Detalhes
            </Header>
            <Grid stackable columns={3}>
              <Grid.Column width={4}>
                <FieldBlock label="Status">
                  {hasRefund
                    ? statusLabelMap[StandaloneChargeStatusEnum.refunded]
                    : statusLabelMap[detail.status] || detail.status}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column width={8}>
                <FieldBlock label="Descrição">
                  <span style={{ whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                    {detail.description || '-'}
                  </span>
                </FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Método">{detail.paymentMethod || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Criado por">{detail.createdByUser?.name || '-'}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Criação">{formatDateTime(detail.createdAt)}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Data de expiração">{formatDateTime(detail.expiresAt)}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Último envio de e-mail">{formatDateTime(detail.sentAt)}</FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Última atualização (registro)">
                  {formatDateTime(detail.updatedAt)}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column width={4}>
                <FieldBlock label="Cancelamento">{formatDateTime(detail.cancelledAt)}</FieldBlock>
              </Grid.Column>
              {detail.status === StandaloneChargeStatusEnum.paid && detail.paidAt && (
                <Grid.Column width={4}>
                  <FieldBlock label="Data de pagamento">{formatDateTime(detail.paidAt)}</FieldBlock>
                </Grid.Column>
              )}
              {estimatedPaidAt && (
                <Grid.Column width={8}>
                  <FieldBlock label="Data estimada de pagamento">
                    <>
                      {estimatedPaidAt}
                      <div style={{ marginTop: 6, fontSize: '0.9em', color: '#666' }}>
                        Derivada da última atualização recebida do gateway (webhook).
                      </div>
                    </>
                  </FieldBlock>
                </Grid.Column>
              )}
            </Grid>

            <Divider section />

            <Header as="h4" style={sectionHeaderStyle}>
              Pagador
            </Header>
            <Grid stackable columns={2}>
              <Grid.Column>
                <FieldBlock label="Nome">{detail.payerName}</FieldBlock>
                <FieldBlock label={getPayerDocumentLabel(detail.payerType)}>
                  {maskCpfOrCnpj(detail.payerCpf)}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="E-mail">{detail.payerEmail}</FieldBlock>
                <FieldBlock label="Telefone">
                  {detail.payerPhone ? maskPhoneNumber(detail.payerPhone) : '-'}
                </FieldBlock>
              </Grid.Column>
            </Grid>

            {showPayerAddressSection(detail) && (
              <>
                <Divider section />
                <Header as="h4" style={sectionHeaderStyle}>
                  Endereço do pagador
                </Header>
                <Grid stackable columns={3}>
                  <Grid.Column width={6}>
                    <FieldBlock label="Endereço">{getPayerAddressLine(payerAddr)}</FieldBlock>
                    <FieldBlock label="Complemento">{payerAddr.complement || '-'}</FieldBlock>
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FieldBlock label="Bairro">{payerAddr.neighborhood || '-'}</FieldBlock>
                    <FieldBlock label="CEP">
                      {payerAddr.zipCode ? maskZipCode(payerAddr.zipCode.replace(/\D/g, '')) : '-'}
                    </FieldBlock>
                  </Grid.Column>
                  <Grid.Column width={5}>
                    <FieldBlock label="Cidade">{payerAddr.city || '-'}</FieldBlock>
                    <FieldBlock label="UF / País">
                      {[payerAddr.state, payerAddr.country].filter(Boolean).join(' / ') || '-'}
                    </FieldBlock>
                  </Grid.Column>
                </Grid>
              </>
            )}

            {!hideEstateSection && (
              <>
                <Divider section />
                <Header as="h4" style={sectionHeaderStyle}>
                  Imobiliária
                </Header>
                <Grid stackable columns={2}>
                  <Grid.Column>
                    <FieldBlock label="Nome">{detail.estate?.name || '-'}</FieldBlock>
                  </Grid.Column>
                  <Grid.Column>
                    <FieldBlock label="ID">
                      <code style={{ fontSize: '0.9em' }}>{detail.estate?.id || '-'}</code>
                    </FieldBlock>
                  </Grid.Column>
                </Grid>
              </>
            )}

            {shouldShowChargeLink && (
              <>
                <Divider section />

                {/* <Header as="h4" style={{ marginBottom: 16 }}>
              Gateway
            </Header>
            <Grid stackable columns={2}>
              <Grid.Column>
                <FieldBlock label="Provedor">{detail.gateway || '-'}</FieldBlock>
                <FieldBlock label="Status no gateway">{detail.gatewayStatus || '-'}</FieldBlock>
                <FieldBlock label="Último evento (tipo)">
                  {detail.lastGatewayPayload?.lastEventType || '-'}
                </FieldBlock>
              </Grid.Column>
              <Grid.Column>
                <FieldBlock label="ID no gateway">
                  <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                    {detail.gatewayChargeId || '-'}
                  </code>
                </FieldBlock>
                <FieldBlock label="Último evento (id)">
                  <code style={{ fontSize: '0.85em', wordBreak: 'break-all' }}>
                    {detail.lastGatewayPayload?.lastEventId || '-'}
                  </code>
                </FieldBlock>
                <FieldBlock label="Última atualização (gateway)">
                  {formatDateTime(detail.lastGatewayPayload?.lastUpdateAt)}
                </FieldBlock>
              </Grid.Column>
            </Grid>

            <Divider section /> */}

                <Header as="h4" style={sectionHeaderStyle}>
                  Link da cobrança
                </Header>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <Button
                    type="button"
                    size="small"
                    onClick={() => onCopyLink(detail.chargeLinkUrl, detail.id)}
                  >
                    Copiar link
                  </Button>
                  {detail.chargeLinkUrl && (
                    <Button
                      type="button"
                      size="small"
                      basic
                      onClick={() =>
                        window.open(
                          toAbsoluteUrl(detail.chargeLinkUrl),
                          '_blank',
                          'noopener,noreferrer'
                        )
                      }
                    >
                      Abrir em nova aba
                    </Button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button type="button" onClick={onClose}>
          Fechar
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ChargeDetailsModal;
